import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject } from 'rxjs';
import { FeedbackService } from '@app/core/services';
import { UploadedFileModel } from './models/uploaded-file.model';

@Component({
    selector: 'sl-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        AsyncPipe,
    ],
})
export class FileUploadComponent implements OnInit {
	@Input() allowedExtensions: string[] = [];
	@Input() buttonText = 'Upload';
	@Input() buttonType: 'stroked' | 'icon' = 'stroked';
	@Input() isImage = false;
	@Input() isReadOnly = false;
	@Input() readAsText = false;
	@Input() hideFileName = false;
	@Input() set uploadedFile(value: UploadedFileModel) {
		this.uploadedFile$.next(value);
	}
	@Input() maxSize = null;

	@Output() fileUploaded: EventEmitter<UploadedFileModel> = new EventEmitter<UploadedFileModel>();

	@ViewChild('fileUploadInput') fileUploadInput: ElementRef;

	public uploadedFile$ = new BehaviorSubject<UploadedFileModel>(null);

	private readonly imageExtensions = ['.png', '.jpg', '.jpeg'];

	constructor(private readonly feedbackService: FeedbackService) {}

	ngOnInit(): void {
		if (this.isImage && !this.allowedExtensions?.length) {
			this.allowedExtensions = this.imageExtensions;
		}
	}

	openFileBrowser(): void {
		const event = new MouseEvent('click', { bubbles: true });
		this.fileUploadInput.nativeElement.dispatchEvent(event);
	}

	removeFile(): void {
		this.uploadedFile$.next(null);
		this.fileUploaded.emit(null);
	}

	fileChangeEvent(event: Event): void {
		this.removeFile();
		this.feedbackService.clear(this.feedbackService.messages[0]?.id, 'error');
		const target = event.target as HTMLInputElement;
		const files = target.files;
		const maxSizeInMb = this.maxSize/1000000;
		if (files && files[0]) {
			const fileToParse = files[0];
			const fileName = fileToParse.name;
			if (this.allowedExtensions?.length && !this.allowedExtensions.find((ext) => fileName.toLowerCase().endsWith(ext))) {
				this.feedbackService.error('Invalid file type.  Must have an extension of ' + this.allowedExtensions.join(' or '));
				target.value = null;
				return;
			}
			if (this.maxSize !== null && fileToParse.size > this.maxSize) {
				this.feedbackService.error('File size too large, please try again. The maximum file size is ' + maxSizeInMb + ' MB.');
				target.value = null;
				return;
			}
			const reader = new FileReader();

			reader.onload = (e: any) => {
				const uploadedFile: UploadedFileModel = { fileName, file: fileToParse };
				if (this.readAsText) {
					const plainText = e.target.result;
					uploadedFile.filePlainText = plainText;
				} else {
					const fileBase64Encoded = e.target.result.split(',')[1]; //Only care about the text after "base64,"
					uploadedFile.fileBase64Encoded = fileBase64Encoded;
				}
				this.uploadedFile$.next(uploadedFile);
				target.value = null;
				this.fileUploaded.emit(this.uploadedFile$.value);
			};

			if (this.readAsText) {
				reader.readAsText(fileToParse);
			} else {
				reader.readAsDataURL(fileToParse);
			}
		}
	}
}
